import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import "../../styles/Form.css";
import "../../styles/ConsentDocument.css";
import ConsentHistoryBody from "../../components/ConsentHistoryBody";
import DocumentDownloadButton from "../../components/DocumentDownloadButton";

const ConsentHistoryPage = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [username, setUsername] = useState("");
  const [employerName, setEmployerName] = useState("");
  const [isSigned, setIsSigned] = useState(false);
  const [signedDate, setSignedDate] = useState("");
  const documentRef = useRef(null);

  const loggedInUserId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";

  const { id: paramId, whId } = useParams();
  const effectiveUserId = isAdmin && paramId ? paramId : loggedInUserId;

  const navigate = useNavigate();
  const location = useLocation();
  const isSlidePage = location.pathname.startsWith("/slide/");

  // ✅ If you know numeric t_id, set it; else leave null (fallback to name+ref)
  const CONSENT_TASK_ID = 24; // you said this is 24

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const token = localStorage.getItem("token");
        const url = isAdmin
          ? `${API_BASE}/api/users/${effectiveUserId}`
          : `${API_BASE}/api/user/profile`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { first_name, last_name } = res.data;
        setUsername(`${first_name} ${last_name}`);
      } catch (err) {
        console.error("Error fetching user name:", err);
      }
    };

    const fetchEmployer = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/work-history/item/${whId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        setEmployerName(res.data?.wh_name || "Unknown");
      } catch (err) {
        console.error("Failed to fetch employer info", err);
      }
    };

    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${effectiveUserId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const idKey =
          CONSENT_TASK_ID != null ? `${String(CONSENT_TASK_ID)}_${whId}` : null;
        const nameKey = `consent_history_${whId}`;

        const done =
          (idKey && res.data.status?.[idKey] === "completed") ||
          res.data.status?.[nameKey] === "completed";

        setIsSigned(Boolean(done));

        if (done) {
          const date =
            (idKey && res.data.signdates?.[idKey]) ??
            res.data.signdates?.[nameKey] ??
            null;
          setSignedDate(date || "");
        } else {
          setSignedDate("");
        }
      } catch (err) {
        console.error("Error checking task status:", err);
      }
    };

    fetchUserName();
    fetchEmployer();
    fetchTaskStatus();
  }, [API_BASE, effectiveUserId, whId, isAdmin, paramId, CONSENT_TASK_ID]);

  return (
    <>
      {/* Top actions */}
      {/* Top header row */}
      {/* {!isSlidePage && (
        <div className="d-flex align-items-center justify-content-between pt-3 px-4">
          <span style={{ fontSize: "18px", fontWeight: 500 }}>
            Consent to Safety and Performance History
          </span>

          <div>
            <button
              className="btn btn-secondary me-2"
              onClick={() => navigate(`/consent/list/${effectiveUserId}`)}
            >
              View All
            </button>
            <button className="btn btn-secondary" onClick={handleReturn}>
              Return
            </button>
          </div>
        </div>
      )} */}

      {!isSlidePage && (
        <div className="d-flex justify-content-between align-items-center pt-3 px-3">
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Consent to Safety and Performance History
          </span>
          <div className="ms-auto">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={() => navigate(`/consent/list/${effectiveUserId}`)}
            >
              View All
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Return
            </button>
          </div>
        </div>
      )}
      {isSlidePage && (
        <div className="d-flex justify-content-between align-items-center pt-3 px-3">
          <div className="d-flex gap-2 ms-auto">
            <DocumentDownloadButton
              targetRef={documentRef}
              filename={`consent-${username || effectiveUserId}-${employerName || whId}`}
              label="Download"
              marginMm={15}
              capturePaddingPx={0}
              preservedMediaFrame={{ width: 400, height: 171, padding: 6 }}
              footerText="New Way Trucking | Consent to Previous Employment"
              onClone={(_document, clonedTarget) =>
                clonedTarget.classList.add("consent-document-export")
              }
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div
        ref={documentRef}
        className={`form-wrapper my-4 print-area ${isSlidePage ? "slide-narrow" : "px-4"}`}
        style={{ fontSize: "15px", lineHeight: "1.6" }}
      >
        <ConsentHistoryBody
          username={username}
          employerName={employerName}
          signedDate={signedDate}
          isSigned={isSigned}
          whId={whId}
          effectiveUserId={effectiveUserId}
          isAdmin={isAdmin}
          title="Consent to Previous Employment"
          taskId={CONSENT_TASK_ID}
        />
      </div>
    </>
  );
};

export default ConsentHistoryPage;
